import { memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DecimalField } from 'src/modules/common/components/DecimalField/DecimalField.tsx';
import { CalcKindEnum, LocalesEnum, RegionsEnum } from 'src/generated/operations.ts';
import {
    FlowCalculatorModals,
    HandleModalTransferEvent,
    ModalState,
} from 'src/modules/user-input/components/FlowCalculatorModals.tsx';
import { AllowedUnits } from 'src/modules/user-input/calcV100/unitsMapping.ts';
import {
    VolumetricFlowCalculatorInput,
    VolumetricFlowCalculatorUnits,
} from 'src/modules/user-input/calcVolumetricFlow/types.ts';
import { UnitSelector } from 'src/modules/user-input/components/UnitSelector.tsx';
import { CalculatorLayout } from 'src/modules/common/layouts/CalculatorLayout.tsx';
import { CalculatorLeftColumn } from 'src/modules/common/components/CalculatorLeftColumn/CalculatorLeftColumn';
import { CalculatorRightColumn } from 'src/modules/common/components/CalculatorRightColumn/CalculatorRightColumn';
import { CalculatorInputRow } from 'src/modules/common/components/CalculatorInputRow/CalculatorInputRow.tsx';
import { getSteamErrorMessages } from 'src/modules/user-input/calcVolumetricFlow/getSteamErrorMessages.ts';
import { getCalculatedCvKv } from 'src/modules/user-input/calcVolumetricFlow/getCalculatedCvKv.ts';
import { getUnitByName } from 'src/modules/user-input/getUnitByName.ts';
import { useSearchParams } from 'react-router-dom';
import { HandleMaxDpModalTransferEvent, MaxDpModal } from 'src/modules/user-input/components/MaxDpModal.tsx';
import { getUserInputFormFieldKey } from 'src/modules/common/hooks/getUserInputFormFieldKey.ts';
import { Unit } from 'uom';
import { targetUnits } from 'src/modules/user-input/targetUnits.ts';
import { convert } from 'src/modules/user-input/convert.ts';

export type VolumetricFlowCalculatorProps = {
    setError: (error: string) => void;
    separatorTitle: ReactNode;
    currentLocale: LocalesEnum;
    currentRegion: RegionsEnum;
    isModulating: boolean;
    kind: CalcKindEnum;
    input: VolumetricFlowCalculatorInput;
    initialState: VolumetricFlowCalculatorInput;
    allowedUnits: AllowedUnits;
    onChanged: (output: VolumetricFlowCalculatorInput) => void;
}

export const CalcVolumetricFlow = memo(({
    setError,
    separatorTitle,
    currentLocale,
    currentRegion,
    isModulating,
    kind,
    input,
    initialState,
    allowedUnits,
    onChanged,
}: VolumetricFlowCalculatorProps) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [kvsTouched, setKvsTouched] = useState(true);
    const [currentModal, setCurrentModal] = useState<ModalState | null>(null);

    const handleModalClose = useCallback(() => {
        setCurrentModal(null);
    }, []);

    const handleModalOpen = useCallback((modalState: ModalState) => {
        setCurrentModal(modalState);
    }, []);

    const handleModalTransfer = useCallback(({ unit, value, additionals }: HandleModalTransferEvent) => {
        if (currentModal === null) {
            return;
        }

        const defaultUnit = currentModal.field === 'inlet' ||
            currentModal.field === 'outlet' ||
            currentModal.field === 'maxDp'
            ? targetUnits.dpv100 : targetUnits.volumetricFlow;
        const unitName: keyof VolumetricFlowCalculatorUnits = `${currentModal.field}_u`;

        onChanged({
            ...input,
            ...additionals,
            [currentModal.field]: convert(
                value,
                unit,
                getUnitByName(input[unitName], defaultUnit),
            ),
        });
        setCurrentModal(null);
        setKvsTouched(false);
    }, [currentModal, input, onChanged]);

    const handleMaxDpModalTransfer = useCallback(({ value, unit, dpmv } : HandleMaxDpModalTransferEvent) => {
        const additionals = {
            ...input,
            dpmv: dpmv.value,
            dpmv_u: dpmv.unit.name,
        };
        handleModalTransfer({ unit, value, additionals });
    }, [handleModalTransfer, input]);

    const handleInputChange = useCallback((
        key: keyof VolumetricFlowCalculatorInput,
        payload: string,
        isKvsTouched: boolean,
    ) => {
        onChanged({
            ...(isKvsTouched ? initialState : input),
            [key]: payload,
        });
        setKvsTouched(isKvsTouched);
    }, [input, initialState, onChanged]);

    const handleKvsUnitChange = useCallback((value: Unit.Unit<'VolumeFlow'>) => {
        onChanged({
            ...initialState,
            kvs: convert(
                input.kvs,
                getUnitByName(input.kvs_u, targetUnits.volumetricFlow),
                value,
            ),
            kvs_u: value.name,
        });
        setKvsTouched(true);
    }, [input, initialState, onChanged]);

    const errorMessage = useMemo(() => ((kind === CalcKindEnum.Steam && currentRegion !== RegionsEnum.Am)
        ? getSteamErrorMessages(input) : ''), [input, kind, currentRegion]);

    const calculated = useMemo(
        () => {
            if (errorMessage.length > 0) {
                return '';
            }
            if (kind === CalcKindEnum.Flow) {
                return '';
            }
            return kvsTouched ? input.kvs : getCalculatedCvKv(input, kind, isModulating, currentRegion);
        },
        [kvsTouched, input, kind, isModulating, currentRegion, errorMessage],
    );

    useEffect(() => {
        const kindSearchParam = searchParams.get('kind');

        if (kindSearchParam !== kind) {
            searchParams.delete('input');
            searchParams.set('kind', kind);
            setSearchParams(searchParams);
        }
    }, [kind, searchParams, setSearchParams]);

    useEffect(() => {
        if (input.kvs !== calculated) {
            onChanged({
                ...input,
                kvs: calculated,
            });
        }
    }, [calculated, input, onChanged]);

    useEffect(() => {
        setError(errorMessage);
    }, [setError, errorMessage]);

    return (
        <CalculatorLayout>
            {currentRegion !== RegionsEnum.Am && (
                <MaxDpModal
                    allowedUnits={allowedUnits}
                    modalsState={currentModal}
                    dpmv={{
                        unit: getUnitByName<'Pressure'>(input.dpmv_u, allowedUnits.dpmv[0]),
                        value: input.dpmv,
                    }}
                    handleModalClose={handleModalClose}
                    handleModalTransfer={handleMaxDpModalTransfer}
                />
            )}
            <FlowCalculatorModals
                allowedUnits={allowedUnits}
                modalsState={currentModal}
                handleModalClose={handleModalClose}
                handleModalTransfer={handleModalTransfer}
                kind={kind}
            />
            {kind === CalcKindEnum.Steam && (
                <CalculatorLeftColumn title={separatorTitle}>
                    <CalculatorInputRow onClick={() => {
                        handleModalOpen({
                            field: 'mass',
                            unit: getUnitByName(input.mass_u, allowedUnits.massFlow[0]),
                        });
                    }}
                    >
                        <DecimalField
                            labelText={getUserInputFormFieldKey(currentRegion, 'mass')}
                            inputSize="lg"
                            name="mass"
                            value={input.mass}
                            currentLocale={currentLocale}
                            shaded={false}
                            endAdornment={(
                                <UnitSelector
                                    shaded={false}
                                    selected={input.mass_u}
                                    values={allowedUnits.massFlow}
                                    onChange={(payload) => handleInputChange('mass_u', payload.name, false)}
                                />
                            )}
                            onValueChange={(payload) => handleInputChange('mass', payload, false)}
                            autoComplete="off"
                        />
                    </CalculatorInputRow>
                    <CalculatorInputRow onClick={() => {
                        handleModalOpen({
                            field: 'inlet',
                            unit: getUnitByName(input.inlet_u, allowedUnits.inletOutlet[0]),
                        });
                    }}
                    >
                        <DecimalField
                            labelText={getUserInputFormFieldKey(currentRegion, 'inlet')}
                            inputSize="lg"
                            name="inlet"
                            value={input.inlet}
                            currentLocale={currentLocale}
                            shaded={false}
                            endAdornment={(
                                <UnitSelector
                                    shaded={false}
                                    selected={input.inlet_u}
                                    values={allowedUnits.inletOutlet}
                                    onChange={(payload) => handleInputChange('inlet_u', payload.name, false)}
                                />
                            )}
                            onValueChange={(payload) => handleInputChange('inlet', payload, false)}
                            autoComplete="off"
                        />
                    </CalculatorInputRow>
                    {currentRegion !== RegionsEnum.Am && (
                        <CalculatorInputRow onClick={() => {
                            handleModalOpen({
                                field: 'outlet',
                                unit: getUnitByName(input.outlet_u, allowedUnits.inletOutlet[0]),
                            });
                        }}
                        >
                            <DecimalField
                                labelText={getUserInputFormFieldKey(currentRegion, 'outlet')}
                                inputSize="lg"
                                name="outlet"
                                value={input.outlet}
                                currentLocale={currentLocale}
                                shaded={false}
                                endAdornment={(
                                    <UnitSelector
                                        shaded={false}
                                        selected={input.outlet_u}
                                        values={allowedUnits.inletOutlet}
                                        onChange={(payload) => handleInputChange('outlet_u', payload.name, false)}
                                    />
                                )}
                                onValueChange={(payload) => handleInputChange('outlet', payload, false)}
                                autoComplete="off"
                            />
                        </CalculatorInputRow>
                    )}
                </CalculatorLeftColumn>
            )}
            {kind !== CalcKindEnum.Steam && (
                <CalculatorLeftColumn title={separatorTitle}>
                    <CalculatorInputRow onClick={() => {
                        handleModalOpen({
                            field: 'v100',
                            unit: getUnitByName(input.v100_u, allowedUnits.volumetricFlow[0]),
                        });
                    }}
                    >
                        <DecimalField
                            labelText={getUserInputFormFieldKey(currentRegion, 'v100')}
                            inputSize="lg"
                            name="v100"
                            value={input.v100}
                            currentLocale={currentLocale}
                            shaded={false}
                            endAdornment={(
                                <UnitSelector
                                    shaded={false}
                                    selected={input.v100_u}
                                    values={allowedUnits.volumetricFlow}
                                    onChange={(value) => handleInputChange('v100_u', value.name, false)}
                                />
                            )}
                            onValueChange={(value) => handleInputChange('v100', value, false)}
                            autoComplete="off"
                        />
                    </CalculatorInputRow>
                    {kind === CalcKindEnum.Kvs && (
                        <CalculatorInputRow onClick={() => {
                            handleModalOpen({
                                field: 'maxDp',
                                unit: getUnitByName(input.maxDp_u, allowedUnits.dpmv[0]),
                            });
                        }}
                        >
                            <DecimalField
                                labelText={getUserInputFormFieldKey(currentRegion, 'maxDp')}
                                inputSize="lg"
                                name="maxDp"
                                value={input.maxDp}
                                currentLocale={currentLocale}
                                shaded={false}
                                endAdornment={(
                                    <UnitSelector
                                        shaded={false}
                                        selected={input.maxDp_u}
                                        values={allowedUnits.dpmv}
                                        onChange={(payload) => handleInputChange('maxDp_u', payload.name, false)}
                                    />
                                )}
                                onValueChange={(value) => handleInputChange('maxDp', value, false)}
                                autoComplete="off"
                            />
                        </CalculatorInputRow>
                    )}
                </CalculatorLeftColumn>
            )}
            {kind !== CalcKindEnum.Flow && (
                <CalculatorRightColumn>
                    <DecimalField
                        labelText={getUserInputFormFieldKey(currentRegion, 'kvs')}
                        inputSize="lg"
                        name="kvs"
                        value={calculated}
                        currentLocale={currentLocale}
                        shaded={false}
                        endAdornment={(
                            <UnitSelector
                                shaded={false}
                                selected={input.kvs_u}
                                values={allowedUnits.volumetricFlow.slice(0, 1)}
                                onChange={handleKvsUnitChange}
                            />
                        )}
                        onValueChange={(value) => handleInputChange('kvs', value, true)}
                        autoComplete="off"
                    />
                </CalculatorRightColumn>
            )}
        </CalculatorLayout>
    );
});
